import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { SectionHeader } from "../components/Top3";
import { BUSINESSES } from "../lib/businesses";
import { toast } from "sonner";
import { ExternalLink, Save } from "lucide-react";
import MemoryEditor from "../components/MemoryEditor";
import VoiceSelector from "../components/VoiceSelector";

const Settings = () => {
  const [token, setToken] = useState("");
  const [connected, setConnected] = useState(false);
  const [saving, setSaving] = useState(false);
  const [projects, setProjects] = useState([]);

  const [routing, setRouting] = useState({ default_project_id: "", business_map: {} });
  const [routingDirty, setRoutingDirty] = useState(false);
  const [routingSaving, setRoutingSaving] = useState(false);

  const refresh = async () => {
    const { connected } = await api.todoistStatus();
    setConnected(connected);
    if (connected) {
      try {
        const [p, r] = await Promise.all([api.todoistProjects(), api.todoistRouting()]);
        setProjects(p);
        setRouting({
          default_project_id: r.default_project_id || "",
          business_map: r.business_map || {},
        });
      } catch {
        setProjects([]);
      }
    } else {
      setProjects([]);
    }
  };
  useEffect(() => { refresh(); }, []);

  const connect = async (e) => {
    e.preventDefault();
    if (!token.trim()) return;
    setSaving(true);
    try {
      const res = await api.todoistConnect(token.trim());
      toast("Todoist connected", { description: `${res.projects_seen} projects visible.` });
      setToken("");
      refresh();
    } catch (e) {
      toast("Could not connect", { description: e?.response?.data?.detail || "Check your token." });
    } finally {
      setSaving(false);
    }
  };

  const disconnect = async () => {
    await api.todoistDisconnect();
    toast("Todoist disconnected");
    refresh();
  };

  const updateBusinessMap = (biz, projectId) => {
    setRouting((r) => ({ ...r, business_map: { ...r.business_map, [biz]: projectId } }));
    setRoutingDirty(true);
  };

  const saveRouting = async () => {
    setRoutingSaving(true);
    try {
      await api.setTodoistRouting({
        default_project_id: routing.default_project_id || null,
        business_map: routing.business_map,
      });
      toast("Routing saved", { description: "Captures will land in the right project." });
      setRoutingDirty(false);
    } catch {
      toast("Could not save routing");
    } finally {
      setRoutingSaving(false);
    }
  };

  const ProjectSelect = ({ value, onChange, testid, allowInherit = false }) => (
    <select
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="op-input op-mono text-xs py-2"
    >
      <option value="">{allowInherit ? "— USE DEFAULT —" : "— NONE —"}</option>
      {projects.map((p) => (
        <option key={p.id} value={p.id}>
          {p.is_inbox_project ? "Inbox" : p.name}
        </option>
      ))}
    </select>
  );

  return (
    <div className="px-6 md:px-12 py-10 md:py-14 max-w-[1200px] mx-auto space-y-12" data-testid="settings-page">
      <header className="relative">
        <div className="op-microcopy text-[#00E5FF] mb-4">CONFIG · INTEGRATIONS</div>
        <h1 className="text-white text-4xl md:text-6xl font-light tracking-tight mb-3">Integrations.</h1>
        <p className="text-[#A0A5B5] text-lg max-w-2xl">
          Paste API tokens once. They're encrypted at rest and never sent to the browser again.
        </p>
        <div className="op-hairline mt-8" />
      </header>

      <section data-testid="todoist-settings">
        <SectionHeader
          code="01"
          label="TODOIST"
          caption="Pulls active tasks into War Room. Lets Quick Capture write directly to Todoist."
        />
        <div className="op-panel op-panel--glow p-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <span
                className={`w-2.5 h-2.5 ${connected ? "bg-[#00E5FF]" : "bg-[#5E657D]"}`}
                style={{ boxShadow: connected ? "0 0 12px rgba(0,229,255,0.7)" : "none" }}
              />
              <span className="op-tag" data-testid="todoist-status-label">
                {connected ? "CONNECTED" : "NOT CONNECTED"}
              </span>
            </div>
            <a
              href="https://app.todoist.com/app/settings/integrations/developer"
              target="_blank"
              rel="noreferrer"
              className="op-tag hover:text-[#00E5FF] inline-flex items-center gap-1.5"
            >
              GET YOUR TOKEN <ExternalLink size={10} />
            </a>
          </div>

          {!connected ? (
            <form onSubmit={connect} className="space-y-5" data-testid="todoist-connect-form">
              <div>
                <div className="op-microcopy mb-2">PERSONAL API TOKEN</div>
                <input
                  type="password"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  placeholder="Paste your Todoist API token"
                  className="op-input op-mono"
                  data-testid="todoist-token-input"
                  autoComplete="off"
                />
                <div className="op-tag mt-2">TODOIST · SETTINGS · INTEGRATIONS · DEVELOPER · COPY API TOKEN</div>
              </div>
              <div className="flex items-center justify-between pt-2">
                <span className="op-tag">STORED · ENCRYPTED · SERVER-SIDE</span>
                <button
                  type="submit"
                  disabled={saving || !token.trim()}
                  className="op-btn op-btn--primary disabled:opacity-40"
                  data-testid="todoist-connect-submit"
                >
                  {saving ? "VERIFYING…" : "CONNECT"}
                </button>
              </div>
            </form>
          ) : (
            <div className="space-y-6" data-testid="todoist-connected-panel">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="border border-white/[0.06] p-4">
                  <div className="op-tag mb-1">PROJECTS VISIBLE</div>
                  <div className="text-white text-2xl font-light">{projects.length}</div>
                </div>
                <div className="border border-white/[0.06] p-4">
                  <div className="op-tag mb-1">CAPTURE → TODOIST</div>
                  <div className="text-[#00E5FF] op-mono text-sm">ENABLED</div>
                </div>
                <div className="border border-white/[0.06] p-4">
                  <div className="op-tag mb-1">WAR ROOM SYNC</div>
                  <div className="text-[#00E5FF] op-mono text-sm">TODAY · FILTER</div>
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={disconnect}
                  className="op-btn text-[#F5A623] border-[#F5A623]/40 hover:border-[#F5A623]"
                  data-testid="todoist-disconnect"
                >
                  DISCONNECT
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      {connected && (
        <section data-testid="routing-settings">
          <SectionHeader
            code="02"
            label="PROJECT ROUTING"
            caption="Which Todoist project each captured task lands in. Business tags win; default catches the rest."
          />
          <div className="op-panel p-8 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-4 items-center pb-5 border-b border-white/[0.05]">
              <div>
                <div className="op-tag text-[#00E5FF]">DEFAULT PROJECT</div>
                <div className="text-[#A0A5B5] text-xs mt-1">Catches captures with no business tag.</div>
              </div>
              <ProjectSelect
                value={routing.default_project_id}
                onChange={(v) => { setRouting((r) => ({ ...r, default_project_id: v })); setRoutingDirty(true); }}
                testid="routing-default"
              />
            </div>

            <div className="space-y-4">
              <div className="op-microcopy">PER-BUSINESS ROUTING</div>
              {BUSINESSES.map((biz) => (
                <div
                  key={biz}
                  className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-4 items-center"
                >
                  <div className="op-tag">{biz}</div>
                  <ProjectSelect
                    value={routing.business_map[biz]}
                    onChange={(v) => updateBusinessMap(biz, v)}
                    testid={`routing-${biz.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    allowInherit
                  />
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between pt-2">
              <span className="op-tag">
                {routingDirty ? "UNSAVED CHANGES" : "IN SYNC"}
              </span>
              <button
                onClick={saveRouting}
                disabled={!routingDirty || routingSaving}
                data-testid="routing-save"
                className="op-btn op-btn--primary inline-flex items-center gap-2 disabled:opacity-40"
              >
                <Save size={12} />
                {routingSaving ? "SAVING…" : "SAVE ROUTING"}
              </button>
            </div>
          </div>
        </section>
      )}

      <MemoryEditor />
      <VoiceSelector />
    </div>
  );
};

export default Settings;
